import React, { useState, useEffect, useMemo, useCallback } from 'react';
import StorageService from '../../services/StorageService';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, LineChart, Line, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';
import { Calendar, Filter, Loader, TrendingUp, TrendingDown, DollarSign, ShoppingBag, Clock, AlertCircle, Star, Award, ThumbsUp, ThumbsDown, Users, Package, Target, Zap, Lock, Crown } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const COLORS = ['#f97316', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

const OwnerDashboard = ({ restaurantId }) => {
  const [restaurant, setRestaurant] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [dateFilter, setDateFilter] = useState('7');
  const [customRange, setCustomRange] = useState([null, null]);
  const [loading, setLoading] = useState(true);
  const [activeInsight, setActiveInsight] = useState('overview');

  useEffect(() => {
    loadInitialData();
  }, [restaurantId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const rest = await StorageService.getRestaurant(restaurantId);
      if (rest) {
        setRestaurant(rest);
      }
    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasAnalyticsAccess = useMemo(() => {
    if (!restaurant) return false;
    const plan = restaurant.package?.toLowerCase();
    return plan === 'premium' || plan === 'advanced' || plan === 'lite' || plan === 'pro' || plan === 'standard';
  }, [restaurant]);

  const categories = useMemo(() => {
    if (!restaurant?.menu) return [];
    return [...new Set(restaurant.menu.map(item => item.category))];
  }, [restaurant?.menu]);

  const filteredOrders = useMemo(() => {
    if (!restaurant?.orders || !restaurant?.menu) return [];

    const now = new Date();
    let startDate = new Date();

    if (dateFilter === '1') startDate.setDate(now.getDate() - 1);
    else if (dateFilter === '7') startDate.setDate(now.getDate() - 7);
    else if (dateFilter === '30') startDate.setDate(now.getDate() - 30);
    else if (dateFilter === '90') startDate.setDate(now.getDate() - 90);
    else if (dateFilter === 'custom' && customRange[0]) startDate = customRange[0];

    let endDate = dateFilter === 'custom' && customRange[1] ? customRange[1] : now;

    let filtered = restaurant.orders.filter(o => {
      const orderDate = new Date(o.time);
      return orderDate >= startDate && orderDate <= endDate;
    });

    if (categoryFilter !== 'All') {
      const categoryItemIds = restaurant.menu
        .filter(item => item.category === categoryFilter)
        .map(i => i.id);
      
      filtered = filtered
        .map(order => ({
          ...order,
          items: (order.items || []).filter(itemId => categoryItemIds.includes(itemId))
        }))
        .filter(o => o.items.length > 0);
    }

    return filtered;
  }, [restaurant?.orders, restaurant?.menu, categoryFilter, dateFilter, customRange]);

  const analytics = useMemo(() => {
    if (!filteredOrders.length || !restaurant?.menu) {
      return {
        revenue: { current: 0, previous: 0, change: 0 },
        orders: { current: 0, previous: 0, change: 0 },
        avgOrder: { current: 0, previous: 0, change: 0 },
        topItems: [],
        bottomItems: [],
        categoryBreakdown: [],
        timeDistribution: [],
        revenueByDay: [],
        peakHours: [],
        insights: []
      };
    }

    const now = new Date();
    const currentPeriodOrders = filteredOrders;
    
    let daysBack = 7;
    if (dateFilter === '1') daysBack = 1;
    else if (dateFilter === '30') daysBack = 30;
    else if (dateFilter === '90') daysBack = 90;
    
    const previousStart = new Date(now.getTime() - (daysBack * 2 * 24 * 60 * 60 * 1000));
    const previousEnd = new Date(now.getTime() - (daysBack * 24 * 60 * 60 * 1000));
    
    const previousOrders = restaurant.orders.filter(o => {
      const orderDate = new Date(o.time);
      return orderDate >= previousStart && orderDate <= previousEnd;
    });

    const currentRevenue = currentPeriodOrders.reduce((s, o) => s + (o.total || 0), 0);
    const previousRevenue = previousOrders.reduce((s, o) => s + (o.total || 0), 0);
    const revenueChange = previousRevenue ? ((currentRevenue - previousRevenue) / previousRevenue * 100) : 0;

    const currentOrderCount = currentPeriodOrders.length;
    const previousOrderCount = previousOrders.length;
    const orderChange = previousOrderCount ? ((currentOrderCount - previousOrderCount) / previousOrderCount * 100) : 0;

    const currentAvg = currentOrderCount ? currentRevenue / currentOrderCount : 0;
    const previousAvg = previousOrderCount ? previousRevenue / previousOrderCount : 0;
    const avgChange = previousAvg ? ((currentAvg - previousAvg) / previousAvg * 100) : 0;

    const itemStats = {};
    currentPeriodOrders.forEach(order => {
      if (order.itemDetails && Array.isArray(order.itemDetails)) {
        order.itemDetails.forEach(item => {
          const baseItemName = item.name
            .replace(/\s*\(Full\)\s*$/, '')
            .replace(/\s*\(Half\)\s*$/, '')
            .toLowerCase()
            .trim();
          
          const itemKey = item.name + '_' + (item.portion || 'Full');
          
          const menuItem = restaurant.menu?.find(m => {
            const menuItemName = m.name.toLowerCase().trim();
            return menuItemName === baseItemName;
          });
          
          const itemCategory = menuItem?.category || item.category || 'Uncategorized';
          
          if (!itemStats[itemKey]) {
            itemStats[itemKey] = {
              id: itemKey,
              name: item.name,
              category: itemCategory,
              price: item.price || 0,
              count: 0,
              revenue: 0
            };
          }
          itemStats[itemKey].count += item.quantity || 1;
          itemStats[itemKey].revenue += (item.price || 0) * (item.quantity || 1);
        });
      } else if (order.items && Array.isArray(order.items)) {
        order.items.forEach(itemId => {
          if (!itemStats[itemId]) {
            const menuItem = restaurant.menu?.find(m => m.id === itemId);
            itemStats[itemId] = {
              id: itemId,
              name: menuItem?.name || 'Unknown',
              category: menuItem?.category || 'Uncategorized',
              price: menuItem?.priceFull || menuItem?.price || 0,
              count: 0,
              revenue: 0
            };
          }
          itemStats[itemId].count++;
          itemStats[itemId].revenue += itemStats[itemId].price;
        });
      }
    });

    const sortedItems = Object.values(itemStats).sort((a, b) => b.count - a.count);
    const topItems = sortedItems.slice(0, 5);
    const bottomItems = sortedItems.slice(-5).reverse();

    const categoryStats = {};
    Object.values(itemStats).forEach(item => {
      if (!categoryStats[item.category]) {
        categoryStats[item.category] = { name: item.category, value: 0, orders: 0 };
      }
      categoryStats[item.category].value += item.revenue;
      categoryStats[item.category].orders += item.count;
    });
    const categoryBreakdown = Object.values(categoryStats);

    const revenueByDay = {};
    currentPeriodOrders.forEach(order => {
      const date = new Date(order.time).toLocaleDateString();
      if (!revenueByDay[date]) {
        revenueByDay[date] = { date, revenue: 0, orders: 0 };
      }
      revenueByDay[date].revenue += order.total || 0;
      revenueByDay[date].orders++;
    });
    const revenueData = Object.values(revenueByDay).sort((a, b) => new Date(a.date) - new Date(b.date));

    const hourStats = {};
    currentPeriodOrders.forEach(order => {
      const hour = new Date(order.time).getHours();
      if (!hourStats[hour]) {
        hourStats[hour] = { hour: hour + ':00', orders: 0, revenue: 0 };
      }
      hourStats[hour].orders++;
      hourStats[hour].revenue += order.total || 0;
    });
    const peakHours = Object.values(hourStats).sort((a, b) => b.orders - a.orders);

    const insights = [];
    
    if (revenueChange > 10) {
      insights.push({
        type: 'success',
        title: 'Revenue Growth',
        message: 'Revenue increased by ' + revenueChange.toFixed(1) + '% compared to previous period!',
        icon: TrendingUp
      });
    } else if (revenueChange < -10) {
      insights.push({
        type: 'warning',
        title: 'Revenue Decline',
        message: 'Revenue decreased by ' + Math.abs(revenueChange).toFixed(1) + '%. Consider promotions or menu adjustments.',
        icon: TrendingDown
      });
    }

    if (topItems.length > 0) {
      insights.push({
        type: 'info',
        title: 'Best Seller',
        message: topItems[0].name + ' is your top performer with ' + topItems[0].count + ' orders!',
        icon: Award
      });
    }

    if (bottomItems.length > 0 && bottomItems[0].count < 2) {
      insights.push({
        type: 'suggestion',
        title: 'Low Performance',
        message: 'Consider removing or updating ' + bottomItems[0].name + ' - only ' + bottomItems[0].count + ' order(s).',
        icon: ThumbsDown
      });
    }

    if (peakHours.length > 0) {
      insights.push({
        type: 'info',
        title: 'Peak Hours',
        message: 'Most orders at ' + peakHours[0].hour + '. Ensure adequate staff during this time.',
        icon: Clock
      });
    }

    if (currentAvg > 500) {
      insights.push({
        type: 'success',
        title: 'High Average Order',
        message: 'Customers spending ₹' + currentAvg.toFixed(0) + ' on average - great upselling!',
        icon: DollarSign
      });
    }

    return {
      revenue: { current: currentRevenue, previous: previousRevenue, change: revenueChange },
      orders: { current: currentOrderCount, previous: previousOrderCount, change: orderChange },
      avgOrder: { current: currentAvg, previous: previousAvg, change: avgChange },
      topItems,
      bottomItems,
      categoryBreakdown,
      timeDistribution: revenueData,
      revenueByDay: revenueData,
      peakHours: peakHours.slice(0, 6),
      insights
    };
  }, [filteredOrders, restaurant?.menu, restaurant?.orders, dateFilter]);

  const handleCategoryChange = useCallback((e) => setCategoryFilter(e.target.value), []);
  const handleDateChange = useCallback((e) => setDateFilter(e.target.value), []);
  const handleCustomRangeChange = useCallback((dates) => {
    setCustomRange(dates);
    setDateFilter('custom');
  }, []);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 font-semibold">Restaurant not found</p>
        </div>
      </div>
    );
  }

  if (!hasAnalyticsAccess) {
    return (
      <div className="p-4 md:p-6 bg-gradient-to-br from-orange-50 to-yellow-50 min-h-screen">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Analytics Dashboard</h1>
          <p className="text-gray-600 text-sm md:text-base">Advanced insights and reporting</p>
        </div>

        <div className="max-w-4xl mx-auto mt-12">
          <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl shadow-2xl p-8 md:p-12 text-white">
            <div className="text-center mb-8">
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full w-24 h-24 md:w-32 md:h-32 mx-auto mb-6 flex items-center justify-center">
                <Lock className="w-12 h-12 md:w-16 md:h-16" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Analytics Locked</h2>
              <p className="text-lg md:text-xl text-white text-opacity-90 mb-2">
                You are currently on the <span className="font-bold">{restaurant.package || 'Basic'} Plan</span>
              </p>
              <p className="text-white text-opacity-80">
                Upgrade to Premium or Advanced to unlock powerful analytics
              </p>
            </div>
<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 hover:shadow-2xl transition-all duration-300 border-4 border-blue-400 shadow-xl transform hover:scale-105">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-500 p-3 rounded-lg shadow-lg">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Premium Plan</h3>
                    <p className="text-sm text-blue-600 font-bold">Most Popular</p>
                  </div>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2 text-gray-800 font-medium">
                    <Zap className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-500" />
                    <span>Full Analytics Dashboard</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800 font-medium">
                    <Zap className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-500" />
                    <span>Revenue & Sales Reports</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800 font-medium">
                    <Zap className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-500" />
                    <span>Top Performers Analysis</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800 font-medium">
                    <Zap className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-500" />
                    <span>5 GB Storage</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800 font-medium">
                    <Zap className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-500" />
                    <span>200 Menu Items</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800 font-medium">
                    <Zap className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-500" />
                    <span>Upto 30 tables</span>
                  </li>
                </ul>
                <div className="text-4xl font-extrabold text-gray-900 mb-2">₹999<span className="text-lg font-semibold text-gray-600">/month</span></div>
              </div>

              <div className="bg-gradient-to-br from-yellow-100 via-amber-50 to-orange-50 rounded-xl p-6 hover:shadow-2xl transition-all duration-300 border-4 border-yellow-400 shadow-xl transform hover:scale-105 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-yellow-400 text-gray-900 px-4 py-1 rounded-bl-lg font-bold text-sm">
                  BEST VALUE
                </div>
                <div className="flex items-center gap-3 mb-4 mt-4">
                  <div className="bg-yellow-500 p-3 rounded-lg shadow-lg">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Advanced Plan</h3>
                    <p className="text-sm text-yellow-700 font-bold">Ultimate Package</p>
                  </div>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2 text-gray-800 font-medium">
                    <Zap className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-600" />
                    <span>Everything in Premium</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800 font-medium">
                    <Zap className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-600" />
                    <span>AI-Powered Insights</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800 font-medium">
                    <Zap className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-600" />
                    <span>Unlimited Menu Items</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800 font-medium">
                    <Zap className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-600" />
                    <span>20GB Storage</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800 font-medium">
                    <Zap className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-600" />
                    <span>Priority Support 24/7</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800 font-medium">
                    <Zap className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-600" />
                    <span>Unlimited tables</span>
                  </li>
                </ul>
                <div className="text-4xl font-extrabold text-gray-900 mb-2">₹1999<span className="text-lg font-semibold text-gray-600">/month</span></div>
              </div>
            </div>

            <div className="text-center">
              <a
                href={'mailto:contact@zyppro.com?subject=Upgrade%20Plan%20Request&body=Hi%20Zyppro%20Team,%0D%0A%0D%0AI%20would%20like%20to%20upgrade%20my%20plan.%0D%0A%0D%0ARestaurant:%20' + encodeURIComponent(restaurant.name) + '%0D%0ACurrent%20Plan:%20' + encodeURIComponent(restaurant.package || 'Basic') + '%0D%0A%0D%0APlease%20contact%20me.%0D%0A%0D%0AThank%20you'}
                className="inline-flex items-center gap-2 bg-white text-orange-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg"
              >
                <Crown className="w-6 h-6" />
                Contact Us to Upgrade
              </a>
              <p className="text-sm text-white text-opacity-80 mt-4">
                Or email us at: <a href="mailto:contact@zyppro.com" className="underline font-semibold">contact@zyppro.com</a>
              </p>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-md p-6 text-center opacity-60 relative overflow-hidden">
              <div className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center">
                <Lock className="w-12 h-12 text-white" />
              </div>
              <TrendingUp className="w-12 h-12 text-orange-500 mx-auto mb-3" />
              <h3 className="font-bold text-gray-800 mb-2">Revenue Trends</h3>
              <p className="text-sm text-gray-600">Track your sales performance over time</p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 text-center opacity-60 relative overflow-hidden">
              <div className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center">
                <Lock className="w-12 h-12 text-white" />
              </div>
              <Award className="w-12 h-12 text-blue-500 mx-auto mb-3" />
              <h3 className="font-bold text-gray-800 mb-2">Top Performers</h3>
              <p className="text-sm text-gray-600">Discover your best-selling items</p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 text-center opacity-60 relative overflow-hidden">
              <div className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center">
                <Lock className="w-12 h-12 text-white" />
              </div>
              <Clock className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="font-bold text-gray-800 mb-2">Peak Hours</h3>
              <p className="text-sm text-gray-600">Optimize staffing based on busy times</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const MetricCard = ({ title, value, change, icon: Icon, prefix }) => {
    const displayPrefix = prefix !== undefined ? prefix : '₹';
    return (
      <div className="bg-white rounded-xl shadow-md p-4 md:p-6 hover:shadow-lg transition">
        <div className="flex items-center justify-between mb-3">
          <div className="bg-orange-100 p-2 md:p-3 rounded-lg">
            <Icon className="w-5 h-5 md:w-6 md:h-6 text-orange-600" />
          </div>
          {change !== 0 && (
            <div className={'flex items-center gap-1 text-xs md:text-sm font-semibold ' + (change > 0 ? 'text-green-600' : 'text-red-600')}>
              {change > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {Math.abs(change).toFixed(1)}%
            </div>
          )}
        </div>
        <p className="text-gray-600 text-xs md:text-sm mb-1">{title}</p>
        <h3 className="text-2xl md:text-3xl font-bold text-gray-800">{displayPrefix}{typeof value === 'number' ? value.toFixed(0) : value}</h3>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 bg-gradient-to-br from-orange-50 to-yellow-50 min-h-screen">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Analytics Dashboard</h1>
            <p className="text-gray-600 text-sm md:text-base">Make data-driven decisions for your restaurant</p>
          </div>
          <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2">
            <Crown className="w-5 h-5" />
            <span className="font-semibold">{restaurant.package}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
          <div className="flex items-center gap-2 flex-1">
            <Filter className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <select 
              value={categoryFilter} 
              onChange={handleCategoryChange} 
              className="flex-1 outline-none px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="All">All Categories</option>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          
          <div className="flex items-center gap-2 flex-1">
            <Calendar className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <select 
              value={dateFilter} 
              onChange={handleDateChange} 
              className="flex-1 outline-none px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="1">Last 24 hours</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          
          {dateFilter === 'custom' && (
            <DatePicker
              selectsRange
              startDate={customRange[0]}
              endDate={customRange[1]}
              onChange={handleCustomRangeChange}
              className="border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 w-full lg:w-auto"
              placeholderText="Select date range"
              isClearable
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard 
          title="Total Revenue" 
          value={analytics.revenue.current} 
          change={analytics.revenue.change}
          icon={DollarSign}
        />
        <MetricCard 
          title="Total Orders" 
          value={analytics.orders.current} 
          change={analytics.orders.change}
          icon={ShoppingBag}
          prefix=""
        />
        <MetricCard 
          title="Avg Order Value" 
          value={analytics.avgOrder.current} 
          change={analytics.avgOrder.change}
          icon={TrendingUp}
        />
        <MetricCard 
          title="Active Items" 
          value={restaurant.menu?.length || 0} 
          change={0}
          icon={Package}
          prefix=""
        />
      </div>

      {analytics.insights.length > 0 && (
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg p-4 md:p-6 mb-6 text-white">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-6 h-6" />
            <h2 className="text-xl md:text-2xl font-bold">Smart Insights</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {analytics.insights.map((insight, idx) => {
              const Icon = insight.icon;
              return (
                <div key={idx} className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-3 md:p-4 hover:bg-opacity-30 transition">
                  <div className="flex items-start gap-3">
                    <div className="bg-white bg-opacity-30 p-2 rounded-lg flex-shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-sm md:text-base mb-1">{insight.title}</h3>
                      <p className="text-xs md:text-sm text-white text-opacity-90">{insight.message}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {['overview', 'items', 'trends', 'performance'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveInsight(tab)}
            className={'px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition ' + (
              activeInsight === tab
                ? 'bg-orange-500 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            )}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeInsight === 'overview' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4">Revenue Trend</h2>
            {analytics.revenueByDay.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={analytics.revenueByDay}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="revenue" stroke="#f97316" fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-500 py-12">No data available</p>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4">Category Performance</h2>
              {analytics.categoryBreakdown.length > 0 ? (
                <div className="space-y-4">
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={analytics.categoryBreakdown}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={90}
                        innerRadius={50}
                        fill="#8884d8"
                        dataKey="value"
                        paddingAngle={2}
                      >
                        {analytics.categoryBreakdown.map((entry, index) => (
                          <Cell key={'cell-' + index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => '₹' + value.toFixed(0)}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {analytics.categoryBreakdown.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-800 truncate">{item.name}</p>
                          <p className="text-xs text-gray-600">₹{item.value.toFixed(0)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-12">No data available</p>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4">Peak Hours</h2>
              {analytics.peakHours.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.peakHours}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="orders" fill="#f97316" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-gray-500 py-12">No data available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeInsight === 'items' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <ThumbsUp className="w-6 h-6 text-green-600" />
              <h2 className="text-lg md:text-xl font-bold text-gray-800">Top Performers</h2>
            </div>
            {analytics.topItems.length > 0 ? (
              <div className="space-y-3">
                {analytics.topItems.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg hover:shadow-md transition">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="bg-green-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                        #{idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 truncate">{item.name}</p>
                        <p className="text-xs text-gray-600">{item.category}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className="text-lg font-bold text-green-600">{item.count}</p>
                      <p className="text-xs text-gray-600">₹{item.revenue}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-12">No data available</p>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <ThumbsDown className="w-6 h-6 text-red-600" />
              <h2 className="text-lg md:text-xl font-bold text-gray-800">Needs Attention</h2>
            </div>
            {analytics.bottomItems.length > 0 ? (
              <div className="space-y-3">
                {analytics.bottomItems.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg hover:shadow-md transition">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 truncate">{item.name}</p>
                        <p className="text-xs text-gray-600">{item.category}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className="text-lg font-bold text-red-600">{item.count}</p>
                      <p className="text-xs text-gray-600">₹{item.revenue}</p>
                    </div>
                  </div>
                ))}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Suggestion:</strong> Consider updating prices, descriptions, or removing items with very low orders to optimize your menu.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-12">No data available</p>
            )}
          </div>
        </div>
      )}

      {activeInsight === 'trends' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4">Sales & Order Trends</h2>
            {analytics.revenueByDay.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={analytics.revenueByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#f97316" strokeWidth={3} name="Revenue (₹)" />
                  <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#3b82f6" strokeWidth={3} name="Orders" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-500 py-12">No data available</p>
            )}
          </div>
        </div>
      )}

      {activeInsight === 'performance' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4">Category Revenue</h2>
            {analytics.categoryBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.categoryBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#f97316" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-500 py-12">No data available</p>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4">Performance Metrics</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-green-500 pl-4 py-2">
                <p className="text-sm text-gray-600 mb-1">Best Category</p>
                <p className="text-xl font-bold text-gray-800">
                  {analytics.categoryBreakdown.length > 0 
                    ? analytics.categoryBreakdown.reduce((a, b) => a.value > b.value ? a : b).name
                    : 'N/A'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  ₹{analytics.categoryBreakdown.length > 0 
                    ? analytics.categoryBreakdown.reduce((a, b) => a.value > b.value ? a : b).value.toFixed(0)
                    : 0} revenue
                </p>
              </div>

              <div className="border-l-4 border-blue-500 pl-4 py-2">
                <p className="text-sm text-gray-600 mb-1">Busiest Hour</p>
                <p className="text-xl font-bold text-gray-800">
                  {analytics.peakHours.length > 0 ? analytics.peakHours[0].hour : 'N/A'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.peakHours.length > 0 ? analytics.peakHours[0].orders : 0} orders
                </p>
              </div>

              <div className="border-l-4 border-purple-500 pl-4 py-2">
                <p className="text-sm text-gray-600 mb-1">Customer Favorites</p>
                <p className="text-xl font-bold text-gray-800">
                  {analytics.topItems.length > 0 ? analytics.topItems[0].name : 'N/A'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.topItems.length > 0 ? analytics.topItems[0].count : 0} orders
                </p>
              </div>

              <div className="border-l-4 border-orange-500 pl-4 py-2">
                <p className="text-sm text-gray-600 mb-1">Revenue Growth</p>
                <p className={'text-xl font-bold ' + (analytics.revenue.change >= 0 ? 'text-green-600' : 'text-red-600')}>
                  {analytics.revenue.change >= 0 ? '+' : ''}{analytics.revenue.change.toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">vs previous period</p>
              </div>

              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-3">
                  <Target className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Action Items</h3>
                    <ul className="space-y-2 text-sm text-gray-700">
                      {analytics.revenue.change < 0 && (
                        <li>• Run promotional campaigns to boost sales</li>
                      )}
                      {analytics.bottomItems.length > 0 && analytics.bottomItems[0].count < 3 && (
                        <li>• Review and update low-performing menu items</li>
                      )}
                      {analytics.topItems.length > 0 && (
                        <li>• Promote &quot;{analytics.topItems[0].name}&quot; more - it is trending!</li>
                      )}
                      {analytics.categoryBreakdown.length > 0 && (
                        <li>• Expand offerings in &quot;{analytics.categoryBreakdown.reduce((a, b) => a.value > b.value ? a : b).name}&quot; category</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {filteredOrders.length === 0 && (
        <div className="bg-white rounded-xl shadow-md p-8 md:p-12 text-center">
          <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-10 h-10 text-orange-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">No Data Available</h3>
          <p className="text-gray-600 mb-4">
            No orders found for the selected filters. Try adjusting your date range or category filter.
          </p>
          <button
            onClick={() => {
              setCategoryFilter('All');
              setDateFilter('30');
            }}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition font-semibold"
          >
            Reset Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default OwnerDashboard;